/**
 * First-party affiliate attribution cookie.
 *
 * Last-touch within a 30-minute TTL. Writes are skipped when `rawUtm` is
 * unchanged, so re-landing on the same affiliate link does not restart the
 * expiry countdown. Only affiliate-medium UTMs are captured — arbitrary
 * marketing UTMs are ignored so they cannot dilute affiliate attribution.
 *
 * The snapshot shape `{ rawUtm: { source, medium, campaign }, capturedAtMs }` is
 * what `checkoutHttp` expects on create-payment-intent and confirm.
 *
 * ## This is an input, never a verdict
 *
 * Everything here is client-controlled: a visitor can type any UTM they like.
 * The cookie only reports what arrived. Which affiliate — if any — actually
 * earns commission is decided server-side by `resolveCheckoutAttribution`
 * against `PromoCodeAssignments` and `AffiliateProfiles`. See
 * `docs/adr/ADR-002-attribution.md`.
 *
 * Renamed from myleaderhealth's `leader_health_attr_v1`: this is a first-party
 * cookie on healberry.com and must not carry another tenant's name.
 */

export const ATTRIBUTION_COOKIE_NAME = 'hb_attr_v1'
const MAX_AGE_SECONDS = 30 * 60

/**
 * @param {unknown} raw
 * @return {string}
 */
export function normalizeUtmSlug(raw) {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

function isSecureContext() {
  if (typeof window === 'undefined') return false
  return window.location.protocol === 'https:'
}

function readCookie(name) {
  if (typeof document === 'undefined') return null
  const prefix = `${name}=`
  const parts = document.cookie.split(';')
  for (const part of parts) {
    const p = part.trim()
    if (p.startsWith(prefix)) {
      try {
        return decodeURIComponent(p.slice(prefix.length))
      } catch {
        return p.slice(prefix.length)
      }
    }
  }
  return null
}

function readCookieRaw() {
  return readCookie(ATTRIBUTION_COOKIE_NAME)
}

function writeCookieRaw(value) {
  if (typeof document === 'undefined') return
  const secure = isSecureContext() ? '; Secure' : ''
  document.cookie = `${ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(value)}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`
}

function isValidSnapshot(parsed) {
  if (!parsed || typeof parsed !== 'object') return false
  const r = parsed.rawUtm
  if (!r || typeof r !== 'object') return false
  return (
    typeof r.source === 'string' &&
    typeof r.medium === 'string' &&
    typeof r.campaign === 'string'
  )
}

function parseStoredSnapshot() {
  const raw = readCookieRaw()
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return isValidSnapshot(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeCookieSnapshot(next) {
  const prev = parseStoredSnapshot()
  if (
    prev &&
    prev.rawUtm.source === next.rawUtm.source &&
    prev.rawUtm.medium === next.rawUtm.medium &&
    prev.rawUtm.campaign === next.rawUtm.campaign
  ) {
    return
  }
  writeCookieRaw(JSON.stringify(next))
}

/**
 * Optional plain `utm_*` cookies (same-site handoffs that strip the query
 * string). Prefer query-string UTMs when present.
 * @return {{ source: string, medium: string, campaign: string } | null}
 */
function readUtmFromPlainCookies() {
  const source = (readCookie('utm_source') || '').trim()
  const medium = (readCookie('utm_medium') || '').trim().toLowerCase()
  const campaign = (readCookie('utm_campaign') || '').trim()
  if (!source || !medium) return null
  return { source, medium, campaign }
}

/**
 * Call on each navigation / load. Prefers UTMs on the query string (freshest
 * last touch) and falls back to plain utm_* cookies. Only affiliate-medium
 * UTMs with a source are captured; general traffic must not overwrite an
 * existing affiliate touch.
 * @param {string} [search]
 */
export function refreshAttributionFromSearchParams(search) {
  if (typeof window === 'undefined') return
  const q = typeof search === 'string' ? search : window.location.search || ''
  const params = new URLSearchParams(q.startsWith('?') ? q : `?${q}`)
  const fromQuery = {
    source: (params.get('utm_source') || '').trim(),
    medium: (params.get('utm_medium') || '').trim().toLowerCase(),
    campaign: (params.get('utm_campaign') || '').trim(),
  }
  const touch =
    fromQuery.source && fromQuery.medium ? fromQuery : readUtmFromPlainCookies()
  if (!touch || touch.medium !== 'affiliate' || !touch.source) return

  // Unchanged UTMs skip the write so re-reading cookies does not reset TTL.
  writeCookieSnapshot({
    rawUtm: {
      source: normalizeUtmSlug(touch.source),
      medium: 'affiliate',
      campaign: touch.campaign,
    },
    capturedAtMs: Date.now(),
  })
}

/**
 * Payload for checkout/promo POST `attributionSnapshot`.
 * @return {{ rawUtm: { source: string, medium: string, campaign: string }, capturedAtMs?: number } | null}
 */
export function readAttributionSnapshotForSubmit() {
  const parsed = parseStoredSnapshot()
  if (!parsed) return null
  const out = {
    rawUtm: {
      source: parsed.rawUtm.source,
      medium: parsed.rawUtm.medium,
      campaign: parsed.rawUtm.campaign,
    },
  }
  if (typeof parsed.capturedAtMs === 'number') {
    out.capturedAtMs = parsed.capturedAtMs
  }
  return out
}

/**
 * If `utm_campaign` is present on the attribution cookie, prefill checkout promo.
 * @return {{ code: string, locked: boolean }}
 */
export function readAttributionPromoPrefill() {
  if (typeof document === 'undefined') return { code: '', locked: false }
  const snap = readAttributionSnapshotForSubmit()
  const campaign =
    snap && typeof snap.rawUtm?.campaign === 'string' ? snap.rawUtm.campaign.trim() : ''
  if (!campaign) return { code: '', locked: false }
  const code = campaign.replace(/\s+/g, '').toUpperCase()
  return code ? { code, locked: true } : { code: '', locked: false }
}
